import React from 'react';
import { css } from '@compiled/react';

import SVG from '@atlaskit/icon/svg';
import type { Size } from '@atlaskit/icon/types';
import { token } from '@atlaskit/tokens';

import { fg } from '@confluence/feature-gating';

const baseStyles = css({
	color: 'currentColor',
	fill: token('color.background.accent.gray.subtler'),
	overflow: 'hidden',
	pointerEvents: 'none',
	/**
	 * Stop-color doesn't properly apply in chrome when the inherited/current color changes.
	 * We have to initially set stop-color to inherit (either via DOM attribute or an initial CSS
	 * rule) and then override it with currentColor for the color changes to be picked up.
	 */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles
	stop: {
		stopColor: 'currentColor',
	},
	width: '32px',
	height: '32px',
});

const SVGContents = (
	<>
		<rect
			x="5"
			y="8"
			width="22"
			height="16"
			rx="2"
			fill={token('color.background.accent.blue.subtlest.hovered')}
		/>
		<rect x="8" y="15" width="16" height="2" rx="1" fill={token('color.link')} />
		<rect x="8" y="11" width="16" height="2" rx="1" fill={token('color.link')} />
		<rect
			x="8"
			y="4"
			width="19"
			height="2"
			rx="1"
			fill={token('color.background.accent.gray.subtlest.pressed')}
		/>
		<rect
			x="8"
			y="30"
			width="19"
			height="2"
			rx="1"
			fill={token('color.background.accent.gray.subtlest.pressed')}
		/>
		<rect
			x="8"
			width="16"
			height="2"
			rx="1"
			fill={token('color.background.accent.gray.subtlest.pressed')}
		/>
		<rect
			x="8"
			y="26"
			width="16"
			height="2"
			rx="1"
			fill={token('color.background.accent.gray.subtlest.pressed')}
		/>
		<rect x="8" y="19" width="10" height="2" rx="1" fill={token('color.link')} />
	</>
);

type IconProps = {
	size?: Size;
	label: string;
};

export const ExcerptMacroIcon = (props: IconProps) =>
	fg('confluence_custom_svg_removal') ? (
		<svg
			viewBox="0 0 32 32"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			data-testid="excerpt-macro-icon"
			css={baseStyles}
			aria-label={props.label || undefined}
			role={props.label ? 'img' : 'presentation'}
		>
			{SVGContents}
		</svg>
	) : (
		<SVG size="large" secondaryColor={token('color.background.accent.gray.subtler')} {...props}>
			{/* Scaling the svg to 24x24 to match the design system. Keeping the viewBox at 32x32 to keep the proportions of the original icon. */}
			<svg
				width="24"
				height="24"
				viewBox="0 0 32 32"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
				data-testid="excerpt-macro-icon"
			>
				{SVGContents}
			</svg>
		</SVG>
	);
